import { css } from '@emotion/react';
import { SMS, TaskStatus } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { TaskCenterTypes, TaskCenterQueries } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { Utils } from '@frontend/task-center-components';
import { useTaskCitation } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import { Accordion, Chip, Heading, NakedButton, SkeletonLoader, Text } from '@frontend/design-system';
import { useGetUserFromUserId } from '../hooks/use-get-user-from-user-id';

type Props = {
  tasks?: SMS['tasks'];
};

type TaskRowProps = {
  taskId: string;
};

const TaskRow = ({ taskId }: TaskRowProps) => {
  const { t } = useTranslation('message-insights-panel');
  const { openTaskWithTaskId } = useTaskCitation();
  const { selectedLocationIds } = useAppScopeStore();

  const { data, isLoading } = TaskCenterQueries.useGetTaskById({
    locationIds: selectedLocationIds,
    taskId,
  });
  const task: TaskCenterTypes.Task | undefined = data?.tasks?.[0];

  const { data: assigneeData, isLoading: isLoadingAssignee } = useGetUserFromUserId(task?.assignee, !!task?.assignee);

  const statusIcon = Utils.getStatusIconName(task?.status);
  const priorityIcon = task?.priority ? Utils.getPriorityIcon(task.priority) : null;
  const dueDate = task?.dueDate ? Utils.formatDate('MMM D, YYYY', task.dueDate) : null;
  const assigneeName = assigneeData ? getFullName(assigneeData) : null;

  if (isLoading) {
    return <SkeletonLoader width='100%' height={56} />;
  }

  if (!task) return null;

  return (
    <NakedButton css={styles.row} onClick={() => openTaskWithTaskId(taskId)}>
      <span css={styles.titleRow}>
        <Icon name={statusIcon.name} size={16} />
        <Text size='medium' weight='bold' css={styles.title}>
          {task.title}
        </Text>
      </span>
      <span css={styles.metaRow}>
        {priorityIcon && (
          <span css={styles.metaItem}>
            <Icon name={priorityIcon.name} color={priorityIcon.color} size={14} />
          </span>
        )}
        {dueDate ? (
          <Chip leftElement={<Icon name='calendar-schedule-back' size={14} color='default' />} variant='neutral'>
            {dueDate}
          </Chip>
        ) : (
          <Text size='small' color='subdued'>
            {t('No Due Date')}
          </Text>
        )}
        <SkeletonLoader isLoading={isLoadingAssignee} width={80} height={14}>
          {assigneeName ? (
            <Chip variant='neutral' leftElement={<Icon name='user' size={14} />}>
              {assigneeName}
            </Chip>
          ) : (
            <Text size='small' color='subdued'>
              {t('Unassigned')}
            </Text>
          )}
        </SkeletonLoader>
      </span>
    </NakedButton>
  );
};

export const TasksAccordion = ({ tasks }: Props) => {
  const { t } = useTranslation('message-insights-panel');
  const taskCount = tasks?.length ?? 0;
  const incompleteCount = tasks?.filter((task) => task.status !== TaskStatus.TASK_STATUS_DONE).length ?? 0;

  return (
    <Accordion.Item value='tasks'>
      <Accordion.Header>
        <Heading level={3} css={styles.headingRow}>
          {t('Tasks')}
          {incompleteCount > 0 && <span css={styles.badge}>{incompleteCount}</span>}
        </Heading>
      </Accordion.Header>
      <Accordion.Body>
        {!taskCount ? (
          <Text color='subdued' size='medium'>
            {t('No tasks for this message')}
          </Text>
        ) : (
          <div css={styles.list}>
            {tasks?.map((smsTask) => (
              <TaskRow key={smsTask.taskId} taskId={smsTask.taskId} />
            ))}
          </div>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

const styles = {
  headingRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  badge: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${theme.spacing(2.5)};
    height: ${theme.spacing(2.5)};
    padding: ${theme.spacing(0, 0.75)};
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.colors.critical50};
    color: ${theme.colors.white};
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.bold};
    line-height: 1;
  `,
  list: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  row: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    width: 100%;
    text-align: left;
    padding: ${theme.spacing(1.5)};
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.neutral20};
    &:hover {
      background: ${theme.colors.neutral5};
    }
  `,
  titleRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  title: css`
    flex: 1;
  `,
  metaRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding-left: ${theme.spacing(3)};
    flex-wrap: wrap;
  `,
  metaItem: css`
    display: flex;
    align-items: center;
  `,
};
